import { Typography, Box } from "@mui/material";
import React, { useEffect, useState } from "react";
import { PieChart, Pie, Cell, Label, Tooltip, ResponsiveContainer } from "recharts";
import { statusColor, INK, ChartTooltip } from "../chartTheme";


export default function IssueAnalysis({ pieChartData }) {

  const [data, setData] = useState([])
  const [labelValue, setLabelValue] = useState(80);


  useEffect(() => {
    getPichartInfo(pieChartData[0]?.IssuecountObj)
    getLableText(pieChartData[0])
  }, [pieChartData])


  const getLableText = async (data) => {
    try {
      const totalIssues = data?.issues?.length || 0;
      const completedIssues = data?.IssuecountObj?.Done || 0;

      // If all issues are moved and the sprint is marked as completed
      if (totalIssues === 0 && data?.completed) {
        setLabelValue(100);
      } else if (totalIssues > 0) {
        const labelText = Math.ceil((completedIssues * 100) / totalIssues);
        setLabelValue(labelText);
      } else {
        setLabelValue(0); // No issues to calculate from
      }
    } catch (error) {
      console.log(error);
    }
  };

  // validated categorical palette, keyed by status name — see ../chartTheme
  const renderColor = (key) => statusColor(key);

  function CustomLabel({ viewBox, value1 = 80, value2 = "Tasks Done" }) {
    const { cx, cy } = viewBox;
    return (
      <text x={cx} y={cy} className="recharts-text recharts-label" textAnchor="middle" dominantBaseline="central">
        <tspan x={cx} dy="-0.35em" fill={INK.primary} fontSize="30" fontWeight="700" letterSpacing="-0.02em">{value1}%</tspan>
        <tspan x={cx} dy="1.9em" fill={INK.muted} fontSize="12" fontWeight="600" letterSpacing="0.06em">{String(value2).toUpperCase()}</tspan>
      </text>
    )
  }

  const totalIssues = data.reduce((sum, item) => sum + (item.value || 0), 0);

  const getPichartInfo = async (IssuecountObj = {}) => {
    try {
      let dataArray = await Promise.all(
        Object.keys(IssuecountObj).map((eachkey) => ({
          name: eachkey,
          value: IssuecountObj[eachkey],
          fill: renderColor(eachkey),
        }))
      );

      if (!pieChartData[0]?.completed) {
        setData(dataArray);
      } else {
        const completedDataArray = dataArray.map((item) =>
          item.name === 'Done' ? { ...item, value: 100 } : item
        );

        setData(completedDataArray);
      }
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <Box sx={{ width: "100%", display: "flex", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
      <Box sx={{ flex: "1 1 240px", minWidth: 240 }}>
        <ResponsiveContainer width="100%" height={260}>
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              startAngle={90}
              endAngle={-270}
              innerRadius={72}
              outerRadius={96}
              dataKey="value"
              /* 2px surface gap between segments */
              paddingAngle={2}
              stroke={INK.surface}
              strokeWidth={2}
              cornerRadius={4}
            >
              {data.map((entry) => (
                <Cell key={entry.name} fill={entry.fill} />
              ))}
              <Label width={30} position="center" content={<CustomLabel value1={labelValue} value2="Tasks Done" />} />
            </Pie>
            <Tooltip content={<ChartTooltip />} cursor={{ fill: "transparent" }} />
          </PieChart>
        </ResponsiveContainer>
      </Box>

      {/* legend doubles as the direct-label relief for the contrast warning:
          every status prints its own count, so identity is never colour-alone */}
      <Box sx={{ flex: "1 1 200px", minWidth: 200 }}>
        {data.map((entry) => (
          <Box
            key={entry.name}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.25,
              py: 0.875,
              borderBottom: `1px solid ${INK.grid}`,
              "&:last-of-type": { borderBottom: 0 },
            }}
          >
            <Box sx={{ width: 10, height: 10, borderRadius: "999px", flex: "0 0 auto", backgroundColor: entry.fill }} />
            <Typography sx={{ fontSize: "13px !important", color: INK.secondary, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {entry.name}
            </Typography>
            <Typography sx={{ fontSize: "13px !important", fontWeight: 650, color: INK.primary, width: 28, textAlign: "right" }}>
              {entry.value}
            </Typography>
            <Typography sx={{ fontSize: "12px !important", color: INK.muted, width: 42, textAlign: "right" }}>
              {totalIssues ? Math.round((entry.value * 100) / totalIssues) : 0}%
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}
